import React, { useState, useEffect, useCallback } from 'react';
import { PersonalCabinetApi, UserProfile, PersonalCompany, PersonalDeposit, CompanyCategory, StatisticsReportPreference } from '../api/PersonalCabinetApi';
import { useAppTranslation } from '../hooks/useAppTranslation';
import { Icons } from '../components/Icons';
import { getImageUrl } from '../utils/imageUtils';
import PasswordChangeModal from '../components/PasswordChangeModal';

// Memoize the InlineNotification component to prevent unnecessary re-renders
const InlineNotification = React.memo<{
  type: 'success' | 'error' | 'info';
  message: string;
  onClose: () => void;
}>(({ type, message, onClose }) => {
  const colors = {
    success: { bg: '#e8f5e8', color: '#2e7d32', border: '#4caf50' },
    error: { bg: '#ffeaea', color: '#c62828', border: '#f44336' },
    info: { bg: '#e3f2fd', color: '#1565c0', border: '#2196f3' }
  };

  React.useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 16px',
      background: colors[type].bg,
      color: colors[type].color,
      border: `1px solid ${colors[type].border}`,
      borderRadius: '8px',
      marginBottom: '16px',
      fontSize: '14px',
      animation: 'slideIn 0.3s ease'
    }}>
      <span>{message}</span>
      <button
        onClick={onClose}
        style={{
          background: 'none',
          border: 'none',
          color: colors[type].color,
          cursor: 'pointer',
          fontSize: '16px'
        }}
      >
        ×
      </button>
    </div>
  );
});

const PersonalCabinetPage: React.FC = () => {
  const { t } = useAppTranslation();
  const [activeTab, setActiveTab] = useState<'profile' | 'reports' | 'companies' | 'deposits' | 'categories'>('profile');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [companies, setCompanies] = useState<PersonalCompany[]>([]);
  const [deposits, setDeposits] = useState<PersonalDeposit[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [categories, setCategories] = useState<CompanyCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CompanyCategory | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  
  // Company management states
  const [editingCompany, setEditingCompany] = useState<PersonalCompany | null>(null);
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [newCompanyData, setNewCompanyData] = useState({ name: '', description: '' });
  const [newCompanyLogo, setNewCompanyLogo] = useState<File | null>(null);
  
  // Deposit management states  
  const [editingDeposit, setEditingDeposit] = useState<PersonalDeposit | null>(null);
  const [showCreateDeposit, setShowCreateDeposit] = useState(false);
  const [newDepositData, setNewDepositData] = useState({ name: '', location: '', description: '' });

  // Notification state
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);

  // Get current user role for permissions
  const currentUserRole = localStorage.getItem('role') || '';
  const isReadOnly = currentUserRole === 'ROLE_VIEWER';
  const canModify = currentUserRole === 'ROLE_ADMIN' || currentUserRole === 'ROLE_WAREHOUSE';

  useEffect(() => {
    loadProfile();
    loadCompanies();
    loadDeposits();
    // Cabinet data is loaded once on entry and refreshed explicitly after mutations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      loadCategories(selectedCompanyId);
    }
    // Company selection is the sole trigger for category loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  // Load functions - removed useCallback to ensure fresh data loads
  const loadProfile = async () => {
    try {
      const response = await PersonalCabinetApi.getProfile();
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to load profile:', error);
      showNotification('error', t('personalCabinet.profileLoadFailed') || 'Failed to load profile.');
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await PersonalCabinetApi.getPersonalCompanies();
      setCompanies(response.data || []);
      if ((response.data || []).length > 0 && !selectedCompanyId) {
        setSelectedCompanyId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
      showNotification('error', t('personalCabinet.companiesLoadFailed') || 'Failed to load companies.');
    }
  };

  const loadDeposits = async () => {
    try {
      const response = await PersonalCabinetApi.getPersonalDeposits();
      setDeposits(response.data || []);
    } catch (error) {
      console.error('Failed to load deposits:', error);
      showNotification('error', t('personalCabinet.depositsLoadFailed') || 'Failed to load deposits.');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async (companyId: number) => {
    try {
      const response = await PersonalCabinetApi.getCompanyCategories(companyId);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
      showNotification('error', t('personalCabinet.categoriesLoadFailed') || 'Failed to load categories.');
    }
  };

  // Memoize notification handlers
  const showNotification = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    setNotification({ type, message });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const handleProfileUpdate = async (updatedProfile: Partial<UserProfile>) => {
    // Optimistically update local state immediately so changes are visible at once
    const previousProfile = profile;
    if (profile) {
      setProfile({ ...profile, ...updatedProfile });
    }
    setEditingProfile(false);
    try {
      const response = await PersonalCabinetApi.updateProfile(updatedProfile);
      setProfile(response.data); // Confirm with server data
      showNotification('success', t('personalCabinet.profileUpdated') || 'Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      setProfile(previousProfile); // Revert on failure
      setEditingProfile(true);
      showNotification('error', t('personalCabinet.profileUpdateFailed') || 'Failed to update profile.');
    }
  };

  const handleAvatarUpload = async (file: File) => {
    try {
      const response = await PersonalCabinetApi.uploadAvatar(file);
      if (profile) {
        setProfile({ ...profile, avatarUrl: response.data });
      }
      showNotification('success', t('personalCabinet.avatarUploaded') || 'Avatar updated');
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      showNotification('error', t('personalCabinet.avatarUploadFailed') || 'Failed to upload avatar.');
    }
  };

  const handleCreateCategory = async () => {
    if (!selectedCompanyId || !newCategoryName.trim()) return;
    const tempId = Date.now() * -1;
    const optimistic: CompanyCategory = { id: tempId, name: newCategoryName, company: { id: selectedCompanyId, name: '' } };
    setCategories(prev => [...prev, optimistic]);
    setNewCategoryName('');
    try {
      const res = await PersonalCabinetApi.createCompanyCategory(selectedCompanyId, { name: optimistic.name });
      setCategories(prev => prev.map(c => c.id === tempId ? res.data : c));
      showNotification('success', t('personalCabinet.categoryCreated') || 'Category created');
    } catch (error) {
      console.error('Failed to create category:', error);
      setCategories(prev => prev.filter(c => c.id !== tempId));
      setNewCategoryName(optimistic.name);
      showNotification('error', t('personalCabinet.categoryCreateFailed') || 'Failed to create category.');
    }
  };

  const handleUpdateCategory = async (category: CompanyCategory, newName: string) => {
    if (!selectedCompanyId) return;
    setCategories(prev => prev.map(c => c.id === category.id ? { ...c, name: newName } : c));
    setEditingCategory(null);
    try {
      const res = await PersonalCabinetApi.updateCompanyCategory(selectedCompanyId, category.id, { name: newName });
      setCategories(prev => prev.map(c => c.id === category.id ? res.data : c));
      showNotification('success', t('personalCabinet.categoryUpdated') || 'Category updated');
    } catch (error) {
      console.error('Failed to update category:', error);
      setCategories(prev => prev.map(c => c.id === category.id ? category : c));
      showNotification('error', t('personalCabinet.categoryUpdateFailed') || 'Failed to update category.');
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (!selectedCompanyId) return;
    const confirmDelete = window.confirm(t('personalCabinet.confirmDeleteCategory') || 'Are you sure you want to delete this category?');
    if (!confirmDelete) return;
    const removed = categories.find(c => c.id === categoryId);
    setCategories(prev => prev.filter(c => c.id !== categoryId));
    try {
      await PersonalCabinetApi.deleteCompanyCategory(selectedCompanyId, categoryId);
      showNotification('info', t('personalCabinet.categoryDeleted') || 'Category deleted');
    } catch (error) {
      console.error('Failed to delete category:', error);
      if (removed) setCategories(prev => [...prev, removed]);
      showNotification('error', t('personalCabinet.categoryDeleteFailed') || 'Failed to delete category.');
    }
  };

  // Company management functions
  const handleCreateCompany = async () => {
    if (!newCompanyData.name.trim()) return;
    
    try {
      const response = await PersonalCabinetApi.createPersonalCompany(newCompanyData);
      console.log('Company created successfully:', response.data);
      
      // Upload logo if provided
      if (newCompanyLogo) {
        try {
          await PersonalCabinetApi.uploadCompanyLogo(response.data.id, newCompanyLogo);
          console.log('Logo uploaded successfully');
        } catch (logoError) {
          console.error('Failed to upload logo:', logoError);
          showNotification('info', t('personalCabinet.companyCreatedButLogoFailed') || 'Company created but logo upload failed');
        }
      }
      
      setNewCompanyData({ name: '', description: '' });
      setNewCompanyLogo(null);
      setShowCreateCompany(false);
      setCompanies(prev => [...prev, response.data]);
      showNotification('success', t('personalCabinet.companyCreated') || 'Company created');
    } catch (error) {
      console.error('Failed to create company:', error);
      showNotification('error', t('personalCabinet.companyCreateFailed') || 'Failed to create company.');
    }
  };

  const handleUpdateCompany = async (company: PersonalCompany, updatedData: { name: string; description?: string }) => {
    setCompanies(prev => prev.map(c => c.id === company.id ? { ...c, ...updatedData } : c));
    setEditingCompany(null);
    try {
      const response = await PersonalCabinetApi.updatePersonalCompany(company.id, updatedData);
      setCompanies(prev => prev.map(c => c.id === company.id ? response.data : c));
      showNotification('success', t('personalCabinet.companyUpdated') || 'Company updated');
    } catch (error) {
      console.error('Failed to update company:', error);
      setCompanies(prev => prev.map(c => c.id === company.id ? company : c));
      showNotification('error', t('personalCabinet.companyUpdateFailed') || 'Failed to update company.');
    }
  };

  const handleDeleteCompany = async (companyId: number) => {
    const confirmDelete = window.confirm(t('personalCabinet.confirmDeleteCompany') || 'Are you sure you want to delete this company? This will remove it from your profile and delete it completely if no other users are associated.');
    if (!confirmDelete) return;
    const removedCompany = companies.find(c => c.id === companyId);
    const removedDeposits = deposits.filter(d => d.company?.id === companyId);
    setCompanies(prev => prev.filter(c => c.id !== companyId));
    setDeposits(prev => prev.filter(d => d.company?.id !== companyId));
    try {
      await PersonalCabinetApi.deletePersonalCompany(companyId);
      showNotification('info', t('personalCabinet.companyDeleted') || 'Company deleted');
    } catch (error) {
      console.error('Failed to delete company:', error);
      if (removedCompany) setCompanies(prev => [...prev, removedCompany]);
      if (removedDeposits.length) setDeposits(prev => [...prev, ...removedDeposits]);
      showNotification('error', t('personalCabinet.companyDeleteFailed') || 'Failed to delete company.');
    }
  };

  const handleCompanyLogoUpload = async (companyId: number, file: File) => {
    try {
      const res = await PersonalCabinetApi.uploadCompanyLogo(companyId, file);
      setCompanies(prev => prev.map(c => c.id === companyId ? { ...c, logoUrl: res.data } : c));
      showNotification('success', t('personalCabinet.logoUploaded') || 'Logo updated');
    } catch (error) {
      console.error('Failed to upload logo:', error);
      showNotification('error', t('personalCabinet.logoUploadFailed') || 'Failed to upload logo.');
    }
  };

  // Deposit management functions
  const handleCreateDeposit = async () => {
    if (!newDepositData.name.trim()) return;
    
    try {
      const response = await PersonalCabinetApi.createPersonalDeposit(newDepositData);
      console.log('Deposit created successfully:', response.data);
      
      setNewDepositData({ name: '', location: '', description: '' });
      setShowCreateDeposit(false);
      setDeposits(prev => [...prev, response.data]);
      showNotification('success', t('personalCabinet.depositCreated') || 'Deposit created');
    } catch (error) {
      console.error('Failed to create deposit:', error);
      showNotification('error', t('personalCabinet.depositCreateFailed') || 'Failed to create deposit.');
    }
  };

  const handleUpdateDeposit = async (deposit: PersonalDeposit, updatedData: { name: string; location?: string; description?: string }) => {
    setDeposits(prev => prev.map(d => d.id === deposit.id ? { ...d, ...updatedData } : d));
    setEditingDeposit(null);
    try {
      const response = await PersonalCabinetApi.updatePersonalDeposit(deposit.id, updatedData);
      setDeposits(prev => prev.map(d => d.id === deposit.id ? response.data : d));
      showNotification('success', t('personalCabinet.depositUpdated') || 'Deposit updated');
    } catch (error) {
      console.error('Failed to update deposit:', error);
      setDeposits(prev => prev.map(d => d.id === deposit.id ? deposit : d));
      showNotification('error', t('personalCabinet.depositUpdateFailed') || 'Failed to update deposit.');
    }
  };

  const handleDeleteDeposit = async (depositId: number) => {
    const confirmDelete = window.confirm(t('personalCabinet.confirmDeleteDeposit') || 'Are you sure you want to delete this deposit?');
    if (!confirmDelete) return;
    const removed = deposits.find(d => d.id === depositId);
    setDeposits(prev => prev.filter(d => d.id !== depositId));
    try {
      await PersonalCabinetApi.deletePersonalDeposit(depositId);
      showNotification('info', t('personalCabinet.depositDeleted') || 'Deposit deleted');
    } catch (error) {
      console.error('Failed to delete deposit:', error);
      if (removed) setDeposits(prev => [...prev, removed]);
      showNotification('error', t('personalCabinet.depositDeleteFailed') || 'Failed to delete deposit.');
    }
  };

  const handleAttachDepositToCompany = async (companyId: number, depositId: number) => {
    try {
      await PersonalCabinetApi.attachDepositToCompany(companyId, depositId);
      console.log('Deposit attached successfully:', { companyId, depositId });
      
      // Force immediate state update and refresh both deposits and companies
      await Promise.all([loadDeposits(), loadCompanies()]);
      console.log('Deposits and companies refreshed after attachment');
      
      showNotification('success', t('personalCabinet.depositAttached') || 'Deposit attached to company');
    } catch (error) {
      console.error('Failed to attach deposit:', error);
      showNotification('error', t('personalCabinet.depositAttachFailed') || 'Failed to attach deposit to company.');
    }
  };

  const handleDetachDepositFromCompany = async (companyId: number, depositId: number) => {
    try {
      await PersonalCabinetApi.detachDepositFromCompany(companyId, depositId);
      console.log('Deposit detached successfully:', { companyId, depositId });
      
      // Force immediate state update and refresh both deposits and companies
      await Promise.all([loadDeposits(), loadCompanies()]);
      console.log('Deposits and companies refreshed after detachment');
      
      showNotification('info', t('personalCabinet.depositDetached') || 'Deposit detached from company');
    } catch (error) {
      console.error('Failed to detach deposit:', error);
      showNotification('error', t('personalCabinet.depositDetachFailed') || 'Failed to detach deposit from company.');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div style={{ color: '#1976d2', fontSize: '18px' }}>
          {t('personalCabinet.loading') || 'Loading...'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      {/* Add CSS animation */}
      <style>
        {`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>

      {/* Notification */}
      {notification && (
        <InlineNotification
          type={notification.type}
          message={notification.message}
          onClose={hideNotification}
        />
      )}

      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        borderRadius: '16px',
        padding: '32px',
        color: 'white',
        marginBottom: '32px',
        display: 'flex',
        alignItems: 'center',
        gap: '24px'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: getImageUrl(profile?.avatarUrl) ? `url(${getImageUrl(profile?.avatarUrl)})` : '#fff',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '32px',
          color: '#1976d2',
          border: '4px solid rgba(255,255,255,0.2)'
        }}>
          {!profile?.avatarUrl && (profile?.username?.[0]?.toUpperCase() || '?')}
        </div>
        <div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '600', color: 'white' }}>
            {t('personalCabinet.welcome') || 'Welcome'}, {profile?.fullName || profile?.username}!
          </h1>
          <p style={{ margin: 0, opacity: 0.9, fontSize: '16px' }}>
            {t('personalCabinet.subtitle') || 'Manage your personal workspace and companies'}
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        marginBottom: '32px',
        borderBottom: '2px solid #f0f0f0',
        paddingBottom: '16px'
      }}>
        {[
          { key: 'profile', label: t('personalCabinet.profile') || 'Profile', icon: Icons.User },
          { key: 'reports', label: t('personalCabinet.emailReports') || 'Email reports', icon: Icons.Mail },
          { key: 'companies', label: t('personalCabinet.companies') || 'Companies', icon: Icons.Building },
          { key: 'deposits', label: t('personalCabinet.deposits') || 'Deposits', icon: Icons.Package },
          { key: 'categories', label: t('personalCabinet.categories') || 'Categories', icon: Icons.Tag }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              border: 'none',
              borderRadius: '12px',
              background: activeTab === tab.key ? '#1976d2' : 'transparent',
              color: activeTab === tab.key ? 'white' : '#666',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {activeTab === 'profile' && (
          <ProfileTab 
            profile={profile}
            editingProfile={editingProfile}
            setEditingProfile={setEditingProfile}
            onProfileUpdate={handleProfileUpdate}
            onAvatarUpload={handleAvatarUpload}
            showNotification={showNotification}
            t={t}
          />
        )}

        {activeTab === 'companies' && (
          <CompaniesTab 
            companies={companies} 
            editingCompany={editingCompany}
            setEditingCompany={setEditingCompany}
            showCreateCompany={showCreateCompany}
            setShowCreateCompany={setShowCreateCompany}
            newCompanyData={newCompanyData}
            setNewCompanyData={setNewCompanyData}
            newCompanyLogo={newCompanyLogo}
            setNewCompanyLogo={setNewCompanyLogo}
            onCreateCompany={handleCreateCompany}
            onUpdateCompany={handleUpdateCompany}
            onDeleteCompany={handleDeleteCompany}
            onLogoUpload={handleCompanyLogoUpload}
            isReadOnly={isReadOnly}
            canModify={canModify}
            t={t} 
          />
        )}

        {activeTab === 'reports' && (
          <StatisticsReportsTab profileEmail={profile?.email || ''} companies={companies} deposits={deposits} showNotification={showNotification} t={t} />
        )}

        {activeTab === 'deposits' && (
          <DepositsTab 
            deposits={deposits} 
            companies={companies}
            editingDeposit={editingDeposit}
            setEditingDeposit={setEditingDeposit}
            showCreateDeposit={showCreateDeposit}
            setShowCreateDeposit={setShowCreateDeposit}
            newDepositData={newDepositData}
            setNewDepositData={setNewDepositData}
            onCreateDeposit={handleCreateDeposit}
            onUpdateDeposit={handleUpdateDeposit}
            onDeleteDeposit={handleDeleteDeposit}
            onAttachToCompany={handleAttachDepositToCompany}
            onDetachFromCompany={handleDetachDepositFromCompany}
            isReadOnly={isReadOnly}
            canModify={canModify}
            t={t} 
          />
        )}

        {activeTab === 'categories' && (
          <CategoriesTab
            companies={companies}
            selectedCompanyId={selectedCompanyId}
            setSelectedCompanyId={setSelectedCompanyId}
            categories={categories}
            editingCategory={editingCategory}
            setEditingCategory={setEditingCategory}
            newCategoryName={newCategoryName}
            setNewCategoryName={setNewCategoryName}
            onCreateCategory={handleCreateCategory}
            onUpdateCategory={handleUpdateCategory}
            onDeleteCategory={handleDeleteCategory}
            isReadOnly={isReadOnly}
            canModify={canModify}
            t={t}
          />
        )}
      </div>
    </div>
  );
};

// Profile Tab Component
const ProfileTab: React.FC<{
  profile: UserProfile | null;
  editingProfile: boolean;
  setEditingProfile: (editing: boolean) => void;
  onProfileUpdate: (profile: Partial<UserProfile>) => void;
  onAvatarUpload: (file: File) => void;
  showNotification: (type: 'success' | 'error' | 'info', message: string) => void;
  t: any;
}> = ({ profile, editingProfile, setEditingProfile, onProfileUpdate, onAvatarUpload, showNotification, t }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    fullName: '',
    phone: ''
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Update form data when profile changes
  React.useEffect(() => {
    if (profile) {
      setFormData({
        username: profile.username || '',
        email: profile.email || '',
        fullName: profile.fullName || '',
        phone: profile.phone || ''
      });
    }
  }, [profile]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onProfileUpdate(formData);
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onAvatarUpload(e.target.files[0]);
    }
  };

  const handlePasswordChangeSuccess = () => {
    showNotification('success', t('personalCabinet.passwordChanged') || 'Password changed successfully!');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ color: '#1976d2', margin: 0 }}>
          {t('personalCabinet.profileSettings') || 'Profile Settings'}
        </h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setShowPasswordModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              border: '1px solid #ff9800',
              borderRadius: '8px',
              background: 'transparent',
              color: '#ff9800',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            <Icons.Lock size={16} />
            {t('personalCabinet.changePassword') || 'Change Password'}
          </button>
          <button
            onClick={() => setEditingProfile(!editingProfile)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              border: '1px solid #1976d2',
              borderRadius: '8px',
              background: editingProfile ? '#1976d2' : 'transparent',
              color: editingProfile ? 'white' : '#1976d2',
              cursor: 'pointer'
            }}
          >
            <Icons.Edit size={16} />
            {editingProfile ? (t('common.cancel') || 'Cancel') : (t('common.edit') || 'Edit')}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '32px' }}>
        {/* Avatar Section */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: getImageUrl(profile?.avatarUrl) ? `url(${getImageUrl(profile?.avatarUrl)})` : '#f0f0f0',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '48px',
            color: '#666',
            border: '4px solid #f0f0f0'
          }}>
            {!profile?.avatarUrl && (profile?.username?.[0]?.toUpperCase() || '?')}
          </div>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#666'
          }}>
            <Icons.Upload size={16} />
            {t('personalCabinet.changeAvatar') || 'Change Avatar'}
            <input
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              style={{ display: 'none' }}
            />
          </label>
        </div>

        {/* Profile Form */}
        <div style={{ flex: 1 }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontWeight: '500', color: '#333' }}>
                  {t('personalCabinet.username') || 'Username'}
                </span>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  disabled={!editingProfile}
                  style={{
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    background: editingProfile ? 'white' : '#f8f8f8'
                  }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontWeight: '500', color: '#333' }}>
                  {t('personalCabinet.email') || 'Email'}
                </span>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!editingProfile}
                  style={{
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    background: editingProfile ? 'white' : '#f8f8f8'
                  }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontWeight: '500', color: '#333' }}>
                  {t('personalCabinet.fullName') || 'Full Name'}
                </span>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  disabled={!editingProfile}
                  style={{
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    background: editingProfile ? 'white' : '#f8f8f8'
                  }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontWeight: '500', color: '#333' }}>
                  {t('personalCabinet.phone') || 'Phone'}
                </span>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  disabled={!editingProfile}
                  style={{
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    background: editingProfile ? 'white' : '#f8f8f8'
                  }}
                />
              </label>
            </div>

            {editingProfile && (
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button
                  type="submit"
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: '8px',
                    background: '#1976d2',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {t('common.save') || 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingProfile(false)}
                  style={{
                    padding: '12px 24px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    background: 'white',
                    color: '#666',
                    cursor: 'pointer'
                  }}
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Password Change Modal */}
      <PasswordChangeModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSuccess={handlePasswordChangeSuccess}
      />
    </div>
  );
};

const StatisticsReportsTab: React.FC<{
  profileEmail: string;
  companies: PersonalCompany[];
  deposits: PersonalDeposit[];
  showNotification: (type: 'success' | 'error' | 'info', message: string) => void;
  t: any;
}> = ({ profileEmail, companies, deposits, showNotification, t }) => {
  const [settings, setSettings] = useState<StatisticsReportPreference>({
    enabled: false,
    frequency: 'WEEKLY',
    scope: 'BOTH',
    recipientEmail: profileEmail,
    companyId: null,
    depositId: null,
    saleNotificationsEnabled: false,
    saleCompanyId: null,
    saleDepositId: null,
    saleRecipientEmail: profileEmail
  });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [saving, setSaving] = useState(false);
  const isWarehouse = localStorage.getItem('role') === 'ROLE_WAREHOUSE';

  useEffect(() => {
    PersonalCabinetApi.getStatisticsReportPreference()
      .then(response => setSettings(response.data))
      .catch(() => showNotification('error', t('personalCabinet.reportLoadFailed')))
      .finally(() => setLoadingSettings(false));
  }, [showNotification, t]);

  const save = async () => {
    if (!settings.recipientEmail.trim()) {
      showNotification('error', t('personalCabinet.reportEmailRequired'));
      return;
    }
    setSaving(true);
    try {
      const response = await PersonalCabinetApi.updateStatisticsReportPreference(settings);
      setSettings(response.data);
      showNotification('success', t('personalCabinet.reportSaved'));
    } catch {
      showNotification('error', t('personalCabinet.reportSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!settings.recipientEmail.trim()) {
      showNotification('error', t('personalCabinet.reportEmailRequired'));
      return;
    }
    setSaving(true);
    try {
      await PersonalCabinetApi.sendStatisticsReportTest(settings);
      showNotification('success', t('personalCabinet.reportTestSent'));
    } catch {
      showNotification('error', t('personalCabinet.reportTestFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loadingSettings) return <div>{t('common.loading')}</div>;
  const fieldStyle: React.CSSProperties = { padding: '12px', border: '1px solid #d8dee9', borderRadius: 8, background: '#fff' };

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ color: '#1976d2', marginTop: 0 }}>{t('personalCabinet.reportSettings')}</h2>
      <p style={{ color: '#667085', lineHeight: 1.6 }}>{t('personalCabinet.reportDescription')}</p>

      <label style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: '#f4f8ff', borderRadius: 10, margin: '22px 0' }}>
        <input type="checkbox" checked={settings.enabled} onChange={e => setSettings({ ...settings, enabled: e.target.checked })} />
        <span style={{ fontWeight: 600 }}>{t('personalCabinet.reportEnabled')}</span>
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ fontWeight: 600 }}>{t('personalCabinet.reportFrequency')}</span>
          <select style={fieldStyle} value={settings.frequency} onChange={e => setSettings({ ...settings, frequency: e.target.value as StatisticsReportPreference['frequency'] })}>
            {(['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'] as const).map(value => <option key={value} value={value}>{t(`personalCabinet.frequency.${value}`)}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ fontWeight: 600 }}>{t('personalCabinet.reportScope')}</span>
          <select style={fieldStyle} value={settings.scope} onChange={e => setSettings({ ...settings, scope: e.target.value as StatisticsReportPreference['scope'] })}>
            {(['COMPANIES', 'DEPOSITS', 'BOTH'] as const).map(value => <option key={value} value={value}>{t(`personalCabinet.scope.${value}`)}</option>)}
          </select>
        </label>
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 18 }}>
        <span style={{ fontWeight: 600 }}>{t('personalCabinet.reportRecipient')}</span>
        <input type="email" style={fieldStyle} value={settings.recipientEmail} onChange={e => setSettings({ ...settings, recipientEmail: e.target.value })} placeholder="name@example.com" />
      </label>

      {settings.scope !== 'DEPOSITS' && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 18 }}>
          <span style={{ fontWeight: 600 }}>{t('personalCabinet.reportCompany')}</span>
          <select style={fieldStyle} value={settings.companyId ?? ''} onChange={e => setSettings({ ...settings, companyId: e.target.value ? Number(e.target.value) : null })}>
            <option value="">{t('personalCabinet.allAssignedCompanies')}</option>
            {companies.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}
          </select>
        </label>
      )}

      {settings.scope !== 'COMPANIES' && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 18 }}>
          <span style={{ fontWeight: 600 }}>{t('personalCabinet.reportDeposit')}</span>
          <select style={fieldStyle} value={settings.depositId ?? ''} onChange={e => setSettings({ ...settings, depositId: e.target.value ? Number(e.target.value) : null })}>
            <option value="">{t('personalCabinet.allAssignedDeposits')}</option>
            {deposits.map(deposit => <option key={deposit.id} value={deposit.id}>{deposit.name}</option>)}
          </select>
        </label>
      )}

      <div style={{ marginTop: 18, padding: 14, background: '#fff8e7', borderRadius: 8, color: '#7a5b00' }}>
        {t('personalCabinet.reportScheduleHelp')}
        {settings.nextSendAt && <div style={{ marginTop: 5 }}>{t('personalCabinet.nextReport')}: {new Date(settings.nextSendAt).toLocaleString()}</div>}
        {settings.lastSentAt && <div>{t('personalCabinet.lastReport')}: {new Date(settings.lastSentAt).toLocaleString()}</div>}
      </div>

      {isWarehouse && (
        <section style={{ marginTop: 28, padding: 20, border: '1px solid #d6e4ff', borderRadius: 14, background: 'linear-gradient(135deg, #f8fbff, #eef5ff)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icons.Mail size={22} />
            <div>
              <h3 style={{ margin: 0, color: '#175cd3' }}>{t('personalCabinet.instantSaleNotifications')}</h3>
              <p style={{ margin: '5px 0 0', color: '#667085', fontSize: 14 }}>{t('personalCabinet.instantSaleDescription')}</p>
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, fontWeight: 600 }}>
            <input type="checkbox" checked={settings.saleNotificationsEnabled} onChange={e => setSettings({ ...settings, saleNotificationsEnabled: e.target.checked })} />
            {t('personalCabinet.notifyEverySale')}
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 16, marginTop: 18 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span style={{ fontWeight: 600 }}>{t('personalCabinet.reportCompany')}</span>
              <select style={fieldStyle} value={settings.saleCompanyId ?? ''} onChange={e => setSettings({ ...settings, saleCompanyId: e.target.value ? Number(e.target.value) : null, saleDepositId: null })}>
                <option value="">{t('personalCabinet.allAssignedCompanies')}</option>
                {companies.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span style={{ fontWeight: 600 }}>{t('personalCabinet.reportDeposit')}</span>
              <select style={fieldStyle} value={settings.saleDepositId ?? ''} onChange={e => setSettings({ ...settings, saleDepositId: e.target.value ? Number(e.target.value) : null })}>
                <option value="">{t('personalCabinet.allAssignedDeposits')}</option>
                {deposits.filter(deposit => !settings.saleCompanyId || deposit.company?.id === settings.saleCompanyId).map(deposit => <option key={deposit.id} value={deposit.id}>{deposit.name}</option>)}
              </select>
            </label>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 16 }}>
            <span style={{ fontWeight: 600 }}>{t('personalCabinet.saleNotificationEmail')}</span>
            <input type="email" style={fieldStyle} value={settings.saleRecipientEmail || ''} onChange={e => setSettings({ ...settings, saleRecipientEmail: e.target.value })} />
          </label>
        </section>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 24 }}>
        <button type="button" disabled={saving} onClick={save} style={{ padding: '11px 22px', border: 0, borderRadius: 8, background: '#1976d2', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
          {saving ? t('common.saving') : t('common.save')}
        </button>
        <button type="button" disabled={saving} onClick={sendTest} style={{ padding: '11px 22px', border: '1px solid #1976d2', borderRadius: 8, background: '#fff', color: '#1976d2', fontWeight: 600, cursor: 'pointer' }}>
          {t('personalCabinet.sendTestReport')}
        </button>
      </div>
    </div>
  );
};

// Companies Tab Component
const CompaniesTab: React.FC<{
  companies: PersonalCompany[];
  editingCompany: PersonalCompany | null;
  setEditingCompany: (company: PersonalCompany | null) => void;
  showCreateCompany: boolean;
  setShowCreateCompany: (show: boolean) => void;
  newCompanyData: { name: string; description: string };
  setNewCompanyData: (data: { name: string; description: string }) => void;
  newCompanyLogo: File | null;
  setNewCompanyLogo: (file: File | null) => void;
  onCreateCompany: () => void;
  onUpdateCompany: (company: PersonalCompany, data: { name: string; description?: string }) => void;
  onDeleteCompany: (companyId: number) => void;
  onLogoUpload: (companyId: number, file: File) => void;
  isReadOnly: boolean;
  canModify: boolean;
  t: any;
}> = ({ 
  companies, 
  editingCompany, 
  setEditingCompany, 
  showCreateCompany, 
  setShowCreateCompany, 
  newCompanyData, 
  setNewCompanyData, 
  newCompanyLogo,
  setNewCompanyLogo,
  onCreateCompany, 
  onUpdateCompany, 
  onDeleteCompany,
  onLogoUpload, 
  isReadOnly,
  canModify,
  t 
}) => {
  const [editingData, setEditingData] = useState({ name: '', description: '' });

  const handleEditStart = (company: PersonalCompany) => {
    setEditingCompany(company);
    setEditingData({ name: company.name, description: company.description || '' });
  };

  const handleEditSave = () => {
    if (editingCompany) {
      onUpdateCompany(editingCompany, editingData);
    }
  };

  const handleLogoChange = (companyId: number, e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onLogoUpload(companyId, e.target.files[0]);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ color: '#1976d2', margin: 0 }}>
          {t('personalCabinet.myCompanies') || 'My Companies'}
          {isReadOnly && <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal', marginLeft: '8px' }}>({t('personalCabinet.viewOnly') || 'View Only'})</span>}
        </h2>
        {canModify && (
          <button
            onClick={() => setShowCreateCompany(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              border: 'none',
              borderRadius: '8px',
              background: '#1976d2',
              color: 'white',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            <Icons.Plus size={16} />
            {t('personalCabinet.createCompany') || 'Create Company'}
          </button>
        )}
      </div>

      {/* Create Company Modal */}
      {showCreateCompany && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            minWidth: '400px',
            maxWidth: '500px'
          }}>
            <h3 style={{ marginTop: 0, color: '#1976d2' }}>
              {t('personalCabinet.createCompany') || 'Create Company'}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Logo Upload Section */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '8px',
                  background: newCompanyLogo ? `url(${URL.createObjectURL(newCompanyLogo)})` : '#f0f0f0',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #ddd',
                  cursor: 'pointer',
                  position: 'relative'
                }}>
                  {!newCompanyLogo && (
                    <Icons.Upload size={24} style={{ color: '#999' }} />
                  )}
                  <label style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                    opacity: 0
                  }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          setNewCompanyLogo(e.target.files[0]);
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontWeight: '500' }}>
                    {t('personalCabinet.companyLogo') || 'Company Logo'}
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                    {t('personalCabinet.clickToUploadLogo') || 'Click to upload logo (optional)'}
                  </p>
                  {newCompanyLogo && (
                    <button
                      type="button"
                      onClick={() => setNewCompanyLogo(null)}
                      style={{
                        marginTop: '4px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        border: '1px solid #f44336',
                        borderRadius: '4px',
                        background: 'white',
                        color: '#f44336',
                        cursor: 'pointer'
                      }}
                    >
                      {t('common.remove') || 'Remove'}
                    </button>
                  )}
                </div>
              </div>
              
              <input
                type="text"
                placeholder={t('personalCabinet.companyName') || 'Company Name'}
                value={newCompanyData.name}
                onChange={(e) => setNewCompanyData({ ...newCompanyData, name: e.target.value })}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px'
                }}
              />
              <textarea
                placeholder={t('personalCabinet.companyDescription') || 'Description (optional)'}
                value={newCompanyData.description}
                onChange={(e) => setNewCompanyData({ ...newCompanyData, description: e.target.value })}
                rows={3}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  resize: 'vertical'
                }}
              />
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowCreateCompany(false)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button
                  onClick={onCreateCompany}
                  disabled={!newCompanyData.name.trim()}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    background: newCompanyData.name.trim() ? '#1976d2' : '#ccc',
                    color: 'white',
                    cursor: newCompanyData.name.trim() ? 'pointer' : 'not-allowed'
                  }}
                >
                  {t('common.create') || 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
        {companies.map(company => (
          <div key={company.id} style={{
            padding: '20px',
            border: '1px solid #eee',
            borderRadius: '12px',
            background: '#fafafa',
            position: 'relative'
          }}>
            {/* Edit Mode */}
            {editingCompany?.id === company.id ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <input
                  type="text"
                  value={editingData.name}
                  onChange={(e) => setEditingData({ ...editingData, name: e.target.value })}
                  style={{
                    padding: '8px 10px',
                    border: '1px solid #1976d2',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                />
                <textarea
                  value={editingData.description}
                  onChange={(e) => setEditingData({ ...editingData, description: e.target.value })}
                  placeholder={t('personalCabinet.companyDescription') || 'Description'}
                  rows={2}
                  style={{
                    padding: '8px 10px',
                    border: '1px solid #1976d2',
                    borderRadius: '6px',
                    resize: 'vertical'
                  }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleEditSave}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: 'none',
                      borderRadius: '6px',
                      background: '#4caf50',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <Icons.Check size={14} />
                  </button>
                  <button
                    onClick={() => setEditingCompany(null)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: 'none',
                      borderRadius: '6px',
                      background: '#f44336',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <Icons.X size={14} />
                  </button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{
                    width: getImageUrl(company.logoUrl) ? '48px' : '80px',
                    height: getImageUrl(company.logoUrl) ? '48px' : '80px',
                    borderRadius: '8px',
                    background: getImageUrl(company.logoUrl) ? `url(${getImageUrl(company.logoUrl)})` : '#1976d2',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: getImageUrl(company.logoUrl) ? '18px' : '32px',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}>
                    {!company.logoUrl && company.name[0]}
                    <label style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      cursor: 'pointer',
                      opacity: 0
                    }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleLogoChange(company.id, e)}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '18px' }}>{company.name}</h3>
                    <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                      {(company.deposits || []).length} {t('personalCabinet.deposits') || 'deposits'}
                    </p>
                  </div>
                  {canModify && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <button
                        onClick={() => handleEditStart(company)}
                        style={{
                          padding: '6px 8px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <Icons.Edit size={16} />
                      </button>
                      <button
                        onClick={() => onDeleteCompany(company.id)}
                        style={{
                          padding: '6px 8px',
                          border: '1px solid #f44336',
                          borderRadius: '6px',
                          background: 'white',
                          color: '#f44336',
                          cursor: 'pointer'
                        }}
                      >
                        <Icons.Trash size={16} />
                      </button>
                    </div>
                  )}
                </div>
                {company.description && (
                  <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                    {company.description}
                  </p>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {companies.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <Icons.Building size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p>{t('personalCabinet.noCompanies') || 'No companies created yet.'}</p>
          <button
            onClick={() => setShowCreateCompany(true)}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: '#1976d2',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            {t('personalCabinet.createFirstCompany') || 'Create Your First Company'}
          </button>
        </div>
      )}
    </div>
  );
};

// Deposits Tab Component
const DepositsTab: React.FC<{
  deposits: PersonalDeposit[];
  companies: PersonalCompany[];
  editingDeposit: PersonalDeposit | null;
  setEditingDeposit: (deposit: PersonalDeposit | null) => void;
  showCreateDeposit: boolean;
  setShowCreateDeposit: (show: boolean) => void;
  newDepositData: { name: string; location: string; description: string };
  setNewDepositData: (data: { name: string; location: string; description: string }) => void;
  onCreateDeposit: () => void;
  onUpdateDeposit: (deposit: PersonalDeposit, data: { name: string; location?: string; description?: string }) => void;
  onDeleteDeposit: (depositId: number) => void;
  onAttachToCompany: (companyId: number, depositId: number) => void;
  onDetachFromCompany: (companyId: number, depositId: number) => void;
  isReadOnly: boolean;
  canModify: boolean;
  t: any;
}> = ({ 
  deposits, 
  companies, 
  editingDeposit, 
  setEditingDeposit, 
  showCreateDeposit, 
  setShowCreateDeposit, 
  newDepositData, 
  setNewDepositData, 
  onCreateDeposit, 
  onUpdateDeposit, 
  onDeleteDeposit, 
  onAttachToCompany, 
  onDetachFromCompany, 
  isReadOnly,
  canModify,
  t 
}) => {
  const [editingData, setEditingData] = useState({ name: '', location: '', description: '' });

  const handleEditStart = (deposit: PersonalDeposit) => {
    setEditingDeposit(deposit);
    setEditingData({ 
      name: deposit.name, 
      location: deposit.location || '', 
      description: deposit.description || '' 
    });
  };

  const handleEditSave = () => {
    if (editingDeposit) {
      onUpdateDeposit(editingDeposit, editingData);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ color: '#1976d2', margin: 0 }}>
          {t('personalCabinet.myDeposits') || 'My Deposits'}
          {isReadOnly && <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal', marginLeft: '8px' }}>({t('personalCabinet.viewOnly') || 'View Only'})</span>}
        </h2>
        {canModify && (
          <button
            onClick={() => setShowCreateDeposit(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              border: 'none',
              borderRadius: '8px',
              background: '#1976d2',
              color: 'white',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            <Icons.Plus size={16} />
            {t('personalCabinet.createDeposit') || 'Create Deposit'}
          </button>
        )}
      </div>

      {/* Create Deposit Modal */}
      {showCreateDeposit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            minWidth: '400px',
            maxWidth: '500px'
          }}>
            <h3 style={{ marginTop: 0, color: '#1976d2' }}>
              {t('personalCabinet.createDeposit') || 'Create Deposit'}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <input
                type="text"
                placeholder={t('personalCabinet.depositName') || 'Deposit Name'}
                value={newDepositData.name}
                onChange={(e) => setNewDepositData({ ...newDepositData, name: e.target.value })}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px'
                }}
              />
              <input
                type="text"
                placeholder={t('personalCabinet.depositLocation') || 'Location (optional)'}
                value={newDepositData.location}
                onChange={(e) => setNewDepositData({ ...newDepositData, location: e.target.value })}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px'
                }}
              />
              <textarea
                placeholder={t('personalCabinet.depositDescription') || 'Description (optional)'}
                value={newDepositData.description}
                onChange={(e) => setNewDepositData({ ...newDepositData, description: e.target.value })}
                rows={3}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  resize: 'vertical'
                }}
              />
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowCreateDeposit(false)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button
                  onClick={onCreateDeposit}
                  disabled={!newDepositData.name.trim()}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    background: newDepositData.name.trim() ? '#1976d2' : '#ccc',
                    color: 'white',
                    cursor: newDepositData.name.trim() ? 'pointer' : 'not-allowed'
                  }}
                >
                  {t('common.create') || 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
        {deposits.map(deposit => (
          <div key={deposit.id} style={{
            padding: '20px',
            border: '1px solid #eee',
            borderRadius: '12px',
            background: '#fafafa',
            position: 'relative'
          }}>
            {/* Edit Mode */}
            {editingDeposit?.id === deposit.id ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <input
                  type="text"
                  value={editingData.name}
                  onChange={(e) => setEditingData({ ...editingData, name: e.target.value })}
                  style={{
                    padding: '8px 10px',
                    border: '1px solid #1976d2',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                />
                <input
                  type="text"
                  value={editingData.location}
                  onChange={(e) => setEditingData({ ...editingData, location: e.target.value })}
                  placeholder={t('personalCabinet.depositLocation') || 'Location'}
                  style={{
                    padding: '8px 10px',
                    border: '1px solid #1976d2',
                    borderRadius: '6px'
                  }}
                />
                <textarea
                  value={editingData.description}
                  onChange={(e) => setEditingData({ ...editingData, description: e.target.value })}
                  placeholder={t('personalCabinet.depositDescription') || 'Description'}
                  rows={2}
                  style={{
                    padding: '8px 10px',
                    border: '1px solid #1976d2',
                    borderRadius: '6px',
                    resize: 'vertical'
                  }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleEditSave}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: 'none',
                      borderRadius: '6px',
                      background: '#4caf50',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <Icons.Check size={14} />
                  </button>
                  <button
                    onClick={() => setEditingDeposit(null)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: 'none',
                      borderRadius: '6px',
                      background: '#f44336',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <Icons.X size={14} />
                  </button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '8px',
                    background: deposit.company ? '#ff9800' : '#4caf50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}>
                    <Icons.Package size={24} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '18px' }}>{deposit.name}</h3>
                    <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                      {deposit.location || t('personalCabinet.noLocation') || 'No location specified'}
                    </p>
                    {deposit.company && (
                      <p style={{ margin: '4px 0 0 0', color: '#1976d2', fontSize: '12px', fontWeight: '500' }}>
                        📍 {deposit.company.name}
                      </p>
                    )}
                  </div>
                  {canModify && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <button
                        onClick={() => handleEditStart(deposit)}
                        style={{
                          padding: '4px 6px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <Icons.Edit size={14} />
                      </button>
                      <button
                        onClick={() => onDeleteDeposit(deposit.id)}
                        style={{
                          padding: '4px 6px',
                          border: '1px solid #f44336',
                          borderRadius: '4px',
                          background: 'white',
                          color: '#f44336',
                          cursor: 'pointer'
                        }}
                      >
                        <Icons.Trash size={14} />
                      </button>
                    </div>
                  )}
                </div>
                
                {deposit.description && (
                  <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '14px' }}>
                    {deposit.description}
                  </p>
                )}

                {/* Company Association */}
                <div style={{ borderTop: '1px solid #eee', paddingTop: '12px' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    {t('personalCabinet.attachToCompany') || 'Attach to Company'}:
                  </p>
                  {deposit.company ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '14px', color: '#1976d2' }}>
                        {deposit.company.name}
                      </span>
                      {canModify && (
                        <button
                          onClick={() => onDetachFromCompany(deposit.company!.id, deposit.id)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #f44336',
                            borderRadius: '4px',
                            background: 'white',
                            color: '#f44336',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {t('personalCabinet.detach') || 'Detach'}
                        </button>
                      )}
                    </div>
                  ) : (
                    canModify ? (
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            onAttachToCompany(Number(e.target.value), deposit.id);
                            e.target.value = '';
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">{t('personalCabinet.selectCompany') || 'Select Company'}</option>
                        {companies.map(company => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                        {t('personalCabinet.notAttached') || 'Not attached to any company'}
                      </span>
                    )
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {deposits.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <Icons.Package size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p>{t('personalCabinet.noDeposits') || 'No deposits created yet.'}</p>
          <button
            onClick={() => setShowCreateDeposit(true)}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: '#1976d2',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            {t('personalCabinet.createFirstDeposit') || 'Create Your First Deposit'}
          </button>
        </div>
      )}
    </div>
  );
};

// Categories Tab Component
const CategoriesTab: React.FC<{
  companies: PersonalCompany[];
  selectedCompanyId: number | null;
  setSelectedCompanyId: (id: number | null) => void;
  categories: CompanyCategory[];
  editingCategory: CompanyCategory | null;
  setEditingCategory: (category: CompanyCategory | null) => void;
  newCategoryName: string;
  setNewCategoryName: (name: string) => void;
  onCreateCategory: () => void;
  onUpdateCategory: (category: CompanyCategory, newName: string) => void;
  onDeleteCategory: (categoryId: number) => void;
  isReadOnly: boolean;
  canModify: boolean;
  t: any;
}> = ({
  companies,
  selectedCompanyId,
  setSelectedCompanyId,
  categories,
  editingCategory,
  setEditingCategory,
  newCategoryName,
  setNewCategoryName,
  onCreateCategory,
  onUpdateCategory,
  onDeleteCategory,
  isReadOnly,
  canModify,
  t
}) => {
  const [editingName, setEditingName] = useState('');

  const handleEditStart = (category: CompanyCategory) => {
    setEditingCategory(category);
    setEditingName(category.name);
  };

  const handleEditSave = () => {
    if (editingCategory) {
      onUpdateCategory(editingCategory, editingName);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ color: '#1976d2', margin: 0 }}>
          {t('personalCabinet.productCategories') || 'Product Categories'}
          {isReadOnly && <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal', marginLeft: '8px' }}>({t('personalCabinet.viewOnly') || 'View Only'})</span>}
        </h2>
        
        <select
          value={selectedCompanyId || ''}
          onChange={(e) => setSelectedCompanyId(Number(e.target.value) || null)}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            background: 'white'
          }}
        >
          <option value="">{t('personalCabinet.selectCompany') || 'Select Company'}</option>
          {companies.map(company => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </select>
      </div>

      {selectedCompanyId && (
        <>
          {/* Add new category */}
          {canModify && (
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginBottom: '24px',
              padding: '16px',
              background: '#f8f9fa',
              borderRadius: '12px'
            }}>
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder={t('personalCabinet.enterCategoryName') || 'Enter category name...'}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px'
                }}
              />
              <button
                onClick={onCreateCategory}
                disabled={!newCategoryName.trim()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  background: newCategoryName.trim() ? '#1976d2' : '#ccc',
                  color: 'white',
                  cursor: newCategoryName.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                <Icons.Plus size={16} />
                {t('personalCabinet.addCategory') || 'Add Category'}
              </button>
            </div>
          )}

          {/* Categories list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {categories.map(category => (
              <div key={category.id} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
                border: '1px solid #eee',
                borderRadius: '12px',
                background: 'white'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    background: '#ff9800',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}>
                    <Icons.Tag size={16} />
                  </div>
                  
                  {editingCategory?.id === category.id ? (
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      style={{
                        padding: '6px 8px',
                        border: '1px solid #1976d2',
                        borderRadius: '4px',
                        fontSize: '16px'
                      }}
                      autoFocus
                    />
                  ) : (
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      {category.name}
                    </span>
                  )}
                </div>

                {canModify && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {editingCategory?.id === category.id ? (
                      <>
                        <button
                          onClick={handleEditSave}
                          style={{
                            padding: '6px 12px',
                            border: 'none',
                            borderRadius: '6px',
                            background: '#4caf50',
                            color: 'white',
                            cursor: 'pointer'
                          }}
                        >
                          <Icons.Check size={14} />
                        </button>
                        <button
                          onClick={() => setEditingCategory(null)}
                          style={{
                            padding: '6px 12px',
                            border: 'none',
                            borderRadius: '6px',
                            background: '#f44336',
                            color: 'white',
                            cursor: 'pointer'
                          }}
                        >
                          <Icons.X size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEditStart(category)}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            background: 'white',
                            cursor: 'pointer'
                          }}
                        >
                          <Icons.Edit size={14} />
                        </button>
                        <button
                          onClick={() => onDeleteCategory(category.id)}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #f44336',
                            borderRadius: '6px',
                            background: 'white',
                            color: '#f44336',
                            cursor: 'pointer'
                          }}
                        >
                          <Icons.Trash size={14} />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {categories.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <Icons.Tag size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>{t('personalCabinet.noCategories') || 'No categories created for this company yet.'}</p>
            </div>
          )}
        </>
      )}

      {!selectedCompanyId && companies.length > 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <Icons.Building size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p>{t('personalCabinet.selectCompanyToManageCategories') || 'Please select a company to manage its categories.'}</p>
        </div>
      )}

      {companies.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <Icons.Building size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p>{t('personalCabinet.noCompaniesForCategories') || 'You need to be assigned to companies to manage categories.'}</p>
        </div>
      )}
    </div>
  );
};

export default PersonalCabinetPage;
